import { useEffect, useMemo, useState } from "react";
import AdminErrorState from "../Components/AdminErrorState";
import { usePermissionCheck } from "../hooks/usePermissionCheck";
import { notifyDialog } from "../utils/dialogs";
import {
  fetchAdminFeedbackTickets,
  fetchAdminFeedbackDetail,
  postAdminFeedbackComment,
  updateAdminFeedbackStatus,
  markFeedbackHelpful,
  setAdminFeedbackLock,
  type AdminFeedbackFilters,
  type AdminFeedbackTicketListItem,
  type AdminFeedbackDetail,
  type FeedbackStatusValue,
  type FeedbackTypeValue,
} from "../api/adminFeedbackApi";

const STATUS_OPTIONS: { value: FeedbackStatusValue; label: string }[] = [
  { value: 0, label: "Açık" },
  { value: 1, label: "İncelemede" },
  { value: 2, label: "Çözüldü" },
  { value: 3, label: "Reddedildi" },
];

const TYPE_OPTIONS: { value: FeedbackTypeValue; label: string }[] = [
  { value: 0, label: "Talep" },
  { value: 1, label: "Öneri" },
  { value: 2, label: "Şikayet" },
  { value: 3, label: "Sistem Geliştirme" },
];

export default function AdminFeedback() {
  const { loading, allowed } = usePermissionCheck("Feedback.View");
  const { allowed: canManage } = usePermissionCheck("Feedback.Manage");
  const { allowed: canComment } = usePermissionCheck("Feedback.Comment");
  const { allowed: canAssign } = usePermissionCheck("Feedback.Assign");
  const [filters, setFilters] = useState<AdminFeedbackFilters>({ page: 1, pageSize: 20 });
  const [tickets, setTickets] = useState<AdminFeedbackTicketListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminFeedbackDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);
  const [statusForm, setStatusForm] = useState({ status: 1 as FeedbackStatusValue, priority: 0, assignedToUserId: "", note: "" });
  const [statusBusy, setStatusBusy] = useState(false);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / (filters.pageSize ?? 20))), [total, filters.pageSize]);

  useEffect(() => {
    if (!allowed) {
      return;
    }
    void loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, filters.page, filters.status, filters.type, filters.search, filters.assignedToUserId, filters.createdByUserId]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    void loadDetail(selectedId);
  }, [selectedId]);

  const loadTickets = async () => {
    setLoadingList(true);
    try {
      const response = await fetchAdminFeedbackTickets(filters);
      setTickets(response.items);
      setTotal(response.total);
      if (response.items.length > 0 && !response.items.some((item) => item.id === selectedId)) {
        setSelectedId(response.items[0].id);
      }
    } catch (error) {
      console.error("[admin] feedback list failed", error);
      await notifyDialog("Kayıtlar yüklenemedi.", "error");
    } finally {
      setLoadingList(false);
    }
  };

  const loadDetail = async (id: string) => {
    setLoadingDetail(true);
    try {
      const data = await fetchAdminFeedbackDetail(id);
      setDetail(data);
      setStatusForm({
        status: data.status,
        priority: data.priority,
        assignedToUserId: data.assignedToUserId ?? "",
        note: "",
      });
    } catch (error) {
      console.error("[admin] feedback detail failed", error);
      await notifyDialog("Detay yüklenemedi.", "error");
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleFilterChange = (key: keyof AdminFeedbackFilters, value: string | number | undefined) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleCommentSubmit = async () => {
    if (!detail || detail.isLocked || !commentText.trim()) {
      return;
    }
    setCommentBusy(true);
    try {
      const comment = await postAdminFeedbackComment(detail.id, commentText.trim());
      setDetail((prev) => (prev ? { ...prev, comments: [...prev.comments, comment] } : prev));
      setCommentText("");
    } catch (error) {
      console.error("[admin] feedback comment failed", error);
      await notifyDialog("Yorum gönderilemedi.", "error");
    } finally {
      setCommentBusy(false);
    }
  };

  const handleStatusSubmit = async () => {
    if (!detail || detail.isLocked) {
      return;
    }
    if (statusForm.assignedToUserId && !canAssign) {
      await notifyDialog("Kayıt atamak için izniniz yok.", "error");
      return;
    }
    setStatusBusy(true);
    try {
      await updateAdminFeedbackStatus(detail.id, {
        status: statusForm.status,
        priority: Number(statusForm.priority) ?? 0,
        assignedToUserId: statusForm.assignedToUserId || undefined,
        note: statusForm.note || undefined,
      });
      await notifyDialog("Talep güncellendi.", "success");
      if (statusForm.status === 2) {
        setDetail(null);
        setSelectedId(null);
      } else {
        void loadDetail(detail.id);
      }
      void loadTickets();
      setStatusForm((prev) => ({ ...prev, note: "" }));
    } catch (error) {
      console.error("[admin] status update failed", error);
      await notifyDialog("Durum güncellenemedi.", "error");
    } finally {
      setStatusBusy(false);
    }
  };

  const handleToggleHelpful = async () => {
    if (!detail || !canManage) {
      return;
    }
    try {
      const nextValue = !detail.isHelpful;
      await markFeedbackHelpful(detail.id, nextValue);
      setDetail((prev) => (prev ? { ...prev, isHelpful: nextValue } : prev));
      setTickets((prev) => prev.map((item) => (item.id === detail.id ? { ...item, isHelpful: nextValue } : item)));
    } catch (error) {
      console.error("[admin] mark helpful failed", error);
      await notifyDialog("Yararlı olarak işaretleme başarısız oldu.", "error");
    }
  };

  const handleToggleLock = async (nextLocked: boolean) => {
    if (!detail || !canManage) {
      return;
    }
    try {
      await setAdminFeedbackLock(detail.id, nextLocked, statusForm.note || undefined);
      setDetail((prev) => (prev ? { ...prev, isLocked: nextLocked } : prev));
      setTickets((prev) => prev.map((item) => (item.id === detail.id ? { ...item, isLocked: nextLocked } : item)));
      setStatusForm((prev) => ({ ...prev, note: "" }));
    } catch (error) {
      console.error("[admin] lock toggle failed", error);
      await notifyDialog("Talep kilidi güncellenemedi.", "error");
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Yetki kontrol ediliyor...</div>;
  }

  if (!allowed) {
    return (
      <AdminErrorState
        status="warning"
        title="Bu sayfayı görüntüleme yetkiniz yok."
        description="Feedback kayıtlarını görmek için gerekli izne sahip değilsiniz. Sistem yöneticinizle iletişime geçin."
      />
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded bg-white p-4 shadow">
        <div className="grid gap-4 md:grid-cols-3">
          <label className="text-xs text-gray-600">
            <div className="mb-1">Durum</div>
            <select
              className="w-full rounded border p-2 text-sm"
              value={filters.status ?? ""}
              onChange={(event) => handleFilterChange("status",event.target.value ? Number(event.target.value) : undefined)}
            >
              <option value="">Tümü</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-gray-600">
            <div className="mb-1">Tür</div>
            <select
              className="w-full rounded border p-2 text-sm"
              value={filters.type ?? ""}
              onChange={(event) => handleFilterChange("type",event.target.value ? Number(event.target.value) : undefined)}
            >
              <option value="">Tümü</option>
              {TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-gray-600">
            <div className="mb-1">Serbest arama</div>
            <input
              className="w-full rounded border p-2 text-sm"
              placeholder="Başlık, referans, kullanıcı"
              value={filters.search ?? ""}
              onChange={(event) => handleFilterChange("search", event.target.value || undefined)}
            />
          </label>
          <label className="text-xs text-gray-600">
            <div className="mb-1">Oluşturan kullanıcı ID</div>
            <input
              className="w-full rounded border p-2 text-sm"
              value={filters.createdByUserId ?? ""}
              onChange={(event) => handleFilterChange("createdByUserId", event.target.value || undefined)}
            />
          </label>
          <label className="text-xs text-gray-600">
            <div className="mb-1">Atanan kullanıcı ID</div>
            <input
              className="w-full rounded border p-2 text-sm"
              value={filters.assignedToUserId ?? ""}
              onChange={(event) => handleFilterChange("assignedToUserId", event.target.value || undefined)}
            />
          </label>
          <div className="flex items-end gap-2">
            <button
              type="button"
              onClick={loadTickets}
              className="w-full rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"
            >
              Filtrele
            </button>
            <button
              type="button"
              onClick={() => setFilters({ page: 1, pageSize: 20 })}
              className="rounded border border-gray-300 px-3 py-2 text-xs text-gray-600 hover:bg-gray-50"
            >
              Sıfırla
            </button>
          </div>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-[1.3fr_1fr]">
        <div className="rounded bg-white p-4 shadow">
          <div className="mb-2 text-xs text-gray-500">
            Toplam {total} kayıt | Sayfa {filters.page ?? 1} / {totalPages}
          </div>
          {loadingList ? (
            <div className="text-sm text-gray-500">Kayıtlar yükleniyor...</div>
          ) : tickets.length === 0 ? (
            <div className="text-sm text-gray-500">Sorgunuza uygun talep bulunamadı.</div>
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket) => {
                const active = ticket.id === selectedId;
                return (
                  <article
                    key={ticket.id}
                    className={`cursor-pointer rounded-2xl border p-4 text-sm shadow-sm ${active ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}
                    onClick={() => setSelectedId(ticket.id)}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="text-xs text-slate-500">{formatDate(ticket.createdAtUtc)}</div>
                        <div className="text-base font-semibold text-slate-900">{ticket.title}</div>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${statusBadge(ticket.status)}`}>
                        {STATUS_OPTIONS.find((option) => option.value === ticket.status)?.label ?? "Durum"}
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-500">
                      <span>{TYPE_OPTIONS.find((option) => option.value === ticket.type)?.label ?? "Tür"}</span>
                      {ticket.sourceSummary && <span>{ticket.sourceSummary}</span>}
                      <span>{ticket.commentCount} yorum</span>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
          {total > (filters.pageSize ?? 20) && (
            <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
              <button
                type="button"
                disabled={(filters.page ?? 1) <= 1}
                onClick={() => setFilters((prev) => ({ ...prev, page: Math.max(1, (prev.page ?? 1) - 1) }))}
                className="rounded border border-slate-300 px-3 py-1 hover:bg-slate-100 disabled:opacity-40"
              >
                Önceki
              </button>
              <button
                type="button"
                disabled={(filters.page ?? 1) >= totalPages}
                onClick={() => setFilters((prev) => ({ ...prev, page: Math.min(totalPages, (prev.page ?? 1) + 1) }))}
                className="rounded border border-slate-300 px-3 py-1 hover:bg-slate-100 disabled:opacity-40"
              >
                Sonraki
              </button>
            </div>
          )}
        </div>

        <div className="rounded bg-white p-4 shadow">
          {loadingDetail ? (
            <div className="text-sm text-gray-500">Detay yükleniyor...</div>
          ) : !detail ? (
            <div className="text-sm text-gray-500">Bir kayıt seçiniz.</div>
          ) : (
            <div className="space-y-4">
              <header>
                <div className="text-xs uppercase text-slate-500">{formatDate(detail.createdAtUtc)}</div>
                <h2 className="text-lg font-semibold text-slate-900">{detail.title}</h2>
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full border border-slate-200 px-3 py-1 text-slate-600">
                    {TYPE_OPTIONS.find((option) => option.value === detail.type)?.label ?? "Tür"}
                  </span>
                  <span className={`rounded-full px-3 py-1 font-semibold ${detail.isHelpful ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                    {detail.isHelpful ? "Yararlı" : "Normal"}
                  </span>
                  {detail.isLocked && <span className="rounded-full bg-rose-100 px-3 py-1 font-semibold text-rose-700">Kilitli</span>}
                </div>
                {detail.sourceSummary && <p className="text-xs text-slate-500">Referans: {detail.sourceSummary}</p>}
                <p className="mt-2 text-sm text-slate-700">{detail.description}</p>
                {canManage && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleToggleHelpful}
                      className="rounded border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                    >
                      {detail.isHelpful ? "Yararlı etiketini kaldır" : "Yararlı olarak işaretle"}
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleToggleLock(!detail.isLocked)}
                      className="rounded border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                    >
                      {detail.isLocked ? "Kilidi aç" : "Kilitle"}
                    </button>
                  </div>
                )}
              </header>

              <div className="rounded-xl border border-slate-200 p-3 text-xs text-slate-500">
                <div>Oluşturan: {detail.creatorName}</div>
                {detail.assignedToName && <div>Atanan: {detail.assignedToName}</div>}
                <div>Öncelik: {detail.priority}</div>
              </div>

              <section>
                <h3 className="mb-2 text-sm font-semibold text-slate-800">Yorumlar ({detail.comments.length})</h3>
                <div className="space-y-2">
                  {detail.comments.length === 0 && <div className="text-xs text-slate-500">Henüz yorum yok.</div>}
                  {detail.comments.map((comment) => (
                    <article key={comment.id} className="rounded-xl border border-slate-200 p-3 text-xs">
                      <div className="flex items-center justify-between text-[11px] text-slate-500">
                        <span>{comment.authorName}</span>
                        <span>{formatDate(comment.createdAtUtc)}</span>
                      </div>
                      <p className="mt-1 text-slate-800">{comment.message}</p>
                      {comment.isModeratorNote && <span className="text-[10px] uppercase text-blue-500">Moderatör</span>}
                    </article>
                  ))}
                </div>
                {canComment && (
                  <div className="mt-3 rounded-xl border border-slate-200 p-3">
                    <textarea
                      className="w-full rounded border border-slate-200 p-2 text-sm"
                      rows={3}
                      placeholder={detail.isLocked ? "Talep kilitli, yorum eklenemez." : "Yanıt yaz"}
                      value={commentText}
                      onChange={(event) => setCommentText(event.target.value)}
                      disabled={detail.isLocked}
                    />
                    <div className="mt-2 flex justify-end">
                      <button
                        type="button"
                        onClick={handleCommentSubmit}
                        disabled={commentBusy || !commentText.trim() || detail.isLocked}
                        className="rounded bg-blue-600 px-4 py-1 text-xs font-semibold text-white hover:bg-blue-500 disabled:opacity-40"
                      >
                        {commentBusy ? "Gönderiliyor" : "Yanıtla"}
                      </button>
                    </div>
                  </div>
                )}
              </section>

              {canManage && (
                <section className="rounded-xl border border-slate-200 p-3 text-sm">
                  <h3 className="mb-2 text-sm font-semibold text-slate-800">Durum güncelle</h3>
                  <label className="block text-xs text-slate-500">
                    Durum
                    <select
                      className="mt-1 w-full rounded border p-2 text-sm"
                      value={statusForm.status}
                      onChange={(event) => setStatusForm((prev) => ({ ...prev, status: Number(event.target.value) as FeedbackStatusValue }))}
                      disabled={detail.isLocked}
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="mt-3 block text-xs text-slate-500">
                    Öncelik (0-5)
                    <input
                      type="number"
                      className="mt-1 w-full rounded border p-2 text-sm"
                      value={statusForm.priority}
                      min={0}
                      max={5}
                      onChange={(event) => setStatusForm((prev) => ({ ...prev, priority: Number(event.target.value) }))}
                      disabled={detail.isLocked}
                    />
                  </label>
                  <label className="mt-3 block text-xs text-slate-500">
                    Atanan kullanıcı ID
                    <input
                      type="text"
                      className="mt-1 w-full rounded border p-2 text-sm"
                      value={statusForm.assignedToUserId}
                      onChange={(event) => setStatusForm((prev) => ({ ...prev, assignedToUserId: event.target.value }))}
                      placeholder={canAssign ? "Kullanıcı id" : "İzin yok"}
                      disabled={!canAssign || detail.isLocked}
                    />
                  </label>
                  <label className="mt-3 block text-xs text-slate-500">
                    Moderatör notu
                    <textarea
                      className="mt-1 w-full rounded border p-2 text-sm"
                      rows={3}
                      value={statusForm.note}
                      onChange={(event) => setStatusForm((prev) => ({ ...prev, note: event.target.value }))}
                      placeholder="İsteğe bağlı"
                    />
                  </label>
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={handleStatusSubmit}
                      disabled={statusBusy || detail.isLocked}
                      className="rounded bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-40"
                    >
                      {statusBusy ? "Kaydediliyor" : "Güncelle"}
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleToggleLock(!detail.isLocked)}
                      className="ml-2 rounded border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-40"
                      disabled={statusBusy}
                    >
                      {detail.isLocked ? "Kilidi aç" : "Kilitle"}
                    </button>
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function formatDate(value?: string) {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function statusBadge(status: FeedbackStatusValue) {
  switch (status) {
    case 0:
      return "bg-amber-100 text-amber-800";
    case 1:
      return "bg-sky-100 text-sky-700";
    case 2:
      return "bg-emerald-100 text-emerald-700";
    case 3:
      return "bg-rose-100 text-rose-700";
    default:
      return "bg-slate-100 text-slate-700";
  }
}
